/**
 * @fileoverview Shared issue request validators for creation, labels,
 * work products, reviewer gates, and run workspace settings.
 *
 * @see doc/product/domains/issues/state-machines.md - status and review constraints
 * @see doc/product/domains/issues/surfaces.md - issue API payload surface
 * @see doc/product/domains/execution/agent-runs.md - run workspace settings used by execution
 */
import { z } from "zod";
import { ISSUE_PRIORITIES, ISSUE_STATUSES } from "../constants.js";
import { organizationEntityReferenceSchema } from "./reference.js";

const runWorkspaceStrategySchema = z
  .object({
    type: z.enum(["project_primary", "git_worktree", "adapter_managed", "cloud_sandbox"]).optional(),
    baseRef: z.string().optional().nullable(),
    branchTemplate: z.string().optional().nullable(),
    worktreeParentDir: z.string().optional().nullable(),
    provisionCommand: z.string().optional().nullable(),
    teardownCommand: z.string().optional().nullable(),
  })
  .strict();

export const issueRunWorkspaceSettingsSchema = z
  .object({
    mode: z.enum(["inherit", "shared_workspace", "isolated_workspace", "operator_branch", "reuse_existing", "agent_default"]).optional(),
    workspaceStrategy: runWorkspaceStrategySchema.optional().nullable(),
    workspaceRuntime: z.record(z.unknown()).optional().nullable(),
  })
  .strict();

/** @deprecated Use issueRunWorkspaceSettingsSchema. */
export const issueExecutionWorkspaceSettingsSchema = issueRunWorkspaceSettingsSchema;

export const issueAssigneeAdapterOverridesSchema = z
  .object({
    agentRuntimeConfig: z.record(z.unknown()).optional(),
    useProjectWorkspace: z.boolean().optional(),
  })
  .strict();

export const createIssueSchema = z.object({
  projectId: organizationEntityReferenceSchema("project", "Project ID").optional().nullable(),
  projectWorkspaceId: z.string().uuid().optional().nullable(),
  goalId: organizationEntityReferenceSchema("goal", "Goal ID").optional().nullable(),
  parentId: organizationEntityReferenceSchema("issue", "Parent issue ID").optional().nullable(),
  title: z.string().min(1),
  description: z.string().optional().nullable(),
  status: z.enum(ISSUE_STATUSES).optional().default("backlog"),
  priority: z.enum(ISSUE_PRIORITIES).optional().default("medium"),
  assigneeAgentId: organizationEntityReferenceSchema("agent", "Assignee agent ID").optional().nullable(),
  assigneeUserId: z.string().optional().nullable(),
  reviewerAgentId: organizationEntityReferenceSchema("agent", "Reviewer agent ID").optional().nullable(),
  reviewerUserId: z.string().optional().nullable(),
  requestDepth: z.number().int().nonnegative().optional().default(0),
  billingCode: z.string().optional().nullable(),
  assigneeAgentRuntimeOverrides: issueAssigneeAdapterOverridesSchema.optional().nullable(),
  runWorkspaceId: z.string().uuid().optional().nullable(),
  runWorkspacePreference: z.enum([
    "inherit",
    "shared_workspace",
    "isolated_workspace",
    "operator_branch",
    "reuse_existing",
    "agent_default",
  ]).optional().nullable(),
  runWorkspaceSettings: issueRunWorkspaceSettingsSchema.optional().nullable(),
  /** @deprecated Use runWorkspaceId. */
  executionWorkspaceId: z.string().uuid().optional().nullable(),
  /** @deprecated Use runWorkspacePreference. */
  executionWorkspacePreference: z.enum([
    "inherit",
    "shared_workspace",
    "isolated_workspace",
    "operator_branch",
    "reuse_existing",
    "agent_default",
  ]).optional().nullable(),
  /** @deprecated Use runWorkspaceSettings. */
  executionWorkspaceSettings: issueRunWorkspaceSettingsSchema.optional().nullable(),
  labelIds: z.array(z.string().uuid()).optional(),
});

export type CreateIssue = z.infer<typeof createIssueSchema>;

export const createRunDebugIssueSchema = z.object({
  diagnostics: z.string().trim().min(1).max(32_000),
});

export type CreateRunDebugIssue = z.infer<typeof createRunDebugIssueSchema>;

export const createIssueLabelSchema = z.object({
  name: z.string().trim().min(1).max(48),
  color: z.string().regex(/^#(?:[0-9a-fA-F]{6})$/, "Color must be a 6-digit hex value"),
});

export type CreateIssueLabel = z.infer<typeof createIssueLabelSchema>;

export const updateIssueLabelSchema = createIssueLabelSchema.partial().refine(
  (value) => value.name !== undefined || value.color !== undefined,
  {
    message: "At least one label field must be provided",
  },
);

export type UpdateIssueLabel = z.infer<typeof updateIssueLabelSchema>;

export const updateIssueSchema = createIssueSchema.partial().extend({
  comment: z.string().min(1).optional(),
  reopen: z.boolean().optional(),
  hiddenAt: z.string().datetime().nullable().optional(),
  reviewDecision: z.enum(["approve", "request_changes", "needs_followup", "blocked"]).optional(),
});

export type UpdateIssue = z.infer<typeof updateIssueSchema>;
export type IssueRunWorkspaceSettings = z.infer<typeof issueRunWorkspaceSettingsSchema>;
/** @deprecated Use IssueRunWorkspaceSettings. */
export type IssueExecutionWorkspaceSettings = IssueRunWorkspaceSettings;

export const reorderIssueSchema = z
  .object({
    issueId: organizationEntityReferenceSchema("issue", "Issue ID"),
    targetStatus: z.enum(ISSUE_STATUSES),
    previousIssueId: organizationEntityReferenceSchema("issue", "Previous issue ID").optional().nullable(),
    nextIssueId: organizationEntityReferenceSchema("issue", "Next issue ID").optional().nullable(),
    position: z.enum(["start", "end"]).optional(),
  })
  .refine(
    (value) => !(value.previousIssueId && value.nextIssueId && value.previousIssueId === value.nextIssueId),
    {
      message: "previousIssueId and nextIssueId must be different",
    },
  );

export type ReorderIssue = z.infer<typeof reorderIssueSchema>;

export const checkoutIssueSchema = z.object({
  agentId: organizationEntityReferenceSchema("agent", "Agent ID"),
  expectedStatuses: z.array(z.enum(ISSUE_STATUSES)).nonempty(),
});

export type CheckoutIssue = z.infer<typeof checkoutIssueSchema>;

export const addIssueCommentSchema = z.object({
  body: z.string().min(1),
  reopen: z.boolean().optional(),
  interrupt: z.boolean().optional(),
  steer: z.object({
    expectedRunId: z.string().uuid(),
  }).optional(),
});

export type AddIssueComment = z.infer<typeof addIssueCommentSchema>;

export const updateIssueCommentSchema = z.object({
  body: z.string().trim().min(1),
});

export type UpdateIssueComment = z.infer<typeof updateIssueCommentSchema>;

export const reportIssueCommitSchema = z.object({
  sha: z.string().trim().regex(/^[0-9a-f]{7,64}$/i, "Commit SHA must be 7 to 64 hexadecimal characters"),
  message: z.string().trim().min(1).max(500),
  branch: z.string().trim().min(1).max(255).optional().nullable(),
  repoPath: z.string().trim().min(1).max(2048).optional().nullable(),
  workspacePath: z.string().trim().min(1).max(2048).optional().nullable(),
  commitCount: z.number().int().positive().max(1000).optional(),
});

export type ReportIssueCommit = z.infer<typeof reportIssueCommitSchema>;

export const linkIssueApprovalSchema = z.object({
  approvalId: z.string().uuid(),
});

export type LinkIssueApproval = z.infer<typeof linkIssueApprovalSchema>;

export const createIssueAttachmentMetadataSchema = z.object({
  issueCommentId: z.string().uuid().optional().nullable(),
  usage: z.enum(["issue", "description_inline", "document_inline", "comment_inline", "comment_attachment"]).optional(),
});

export type CreateIssueAttachmentMetadata = z.infer<typeof createIssueAttachmentMetadataSchema>;

export const createIssueWorkspaceAttachmentSchema = z.object({
  path: z.string().trim().min(1).max(2048),
});

export type CreateIssueWorkspaceAttachment = z.infer<typeof createIssueWorkspaceAttachmentSchema>;

export const ISSUE_DOCUMENT_FORMATS = ["markdown"] as const;

export const issueDocumentFormatSchema = z.enum(ISSUE_DOCUMENT_FORMATS);

export const createLibraryDocumentSchema = z.object({
  title: z.string().trim().max(200).nullable().optional(),
  format: issueDocumentFormatSchema.optional().default("markdown"),
  body: z.string().max(524288).optional().default(""),
  changeSummary: z.string().trim().max(500).nullable().optional(),
});

export const updateLibraryDocumentSchema = z.object({
  title: z.string().trim().max(200).nullable().optional(),
  format: issueDocumentFormatSchema.optional().default("markdown"),
  body: z.string().max(524288),
  changeSummary: z.string().trim().max(500).nullable().optional(),
  baseRevisionId: z.string().uuid().nullable().optional(),
});

export const restoreLibraryDocumentRevisionSchema = z.object({
  changeSummary: z.string().trim().max(500).nullable().optional(),
});

export type CreateLibraryDocument = z.infer<typeof createLibraryDocumentSchema>;
export type UpdateLibraryDocument = z.infer<typeof updateLibraryDocumentSchema>;
export type RestoreLibraryDocumentRevision = z.infer<typeof restoreLibraryDocumentRevisionSchema>;
